import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Loader2, X, Building, ChevronDown } from "lucide-react";
import {
  searchAddresses,
  type AddressAutocompleteResult,
} from "@bcc/shared-api";

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (result: AddressAutocompleteResult) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Enter street address, e.g., 123 Main St NW",
  label,
  required,
  error,
  disabled,
  className = "",
}: AddressAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressAutocompleteResult[]>(
    [],
  );
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasSearched, setHasSearched] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search function
  const searchAddressesDebounced = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const results = await searchAddresses(query, 10);
      setSuggestions(results);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error("Address search error:", err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);

    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounce timeout (300ms)
    debounceRef.current = setTimeout(() => {
      searchAddressesDebounced(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSelect = (result: AddressAutocompleteResult) => {
    onChange(result.address);
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onSelect?.(result);
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === "ArrowDown" && value.length >= 2) {
        setIsOpen(true);
        searchAddressesDebounced(value);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      case "Tab":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle clear button
  const handleClear = () => {
    onChange("");
    setSuggestions([]);
    setIsOpen(false);
    setHasSearched(false);
    inputRef.current?.focus();
  };

  // Handle focus
  const handleFocus = () => {
    if (value.length >= 2 && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-index="${highlightedIndex}"]`,
      );
      highlightedElement?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  const showDropdown =
    isOpen &&
    (isLoading || suggestions.length > 0 || (hasSearched && value.length >= 2));

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input container */}
      <div className="relative">
        {/* Map pin icon */}
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full pl-10 pr-10 py-2.5 rounded-lg border
            bg-white text-gray-900 placeholder:text-gray-400 text-sm
            focus:ring-2 outline-none transition-all duration-200
            ${
              error
                ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                : "border-gray-200 focus:border-teal-500 focus:ring-teal-100"
            }
            ${disabled ? "bg-gray-50 cursor-not-allowed" : ""}
          `}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          role="combobox"
        />

        {/* Right side icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && (
            <Loader2 className="w-4 h-4 text-teal-500 animate-spin" />
          )}
          {!isLoading && value && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Clear address"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {!isLoading && !value && (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Error text */}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
            role="listbox"
          >
            {/* Loading state */}
            {isLoading && (
              <div className="px-4 py-6 text-center">
                <Loader2 className="w-5 h-5 text-teal-500 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Searching addresses...</p>
              </div>
            )}

            {/* No results state */}
            {!isLoading && suggestions.length === 0 && hasSearched && (
              <div className="px-4 py-6 text-center">
                <MapPin className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-600">
                  No addresses found
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Try a different search term
                </p>
              </div>
            )}

            {/* Results list */}
            {!isLoading && suggestions.length > 0 && (
              <ul className="max-h-60 overflow-y-auto py-1">
                {suggestions.map((result, index) => (
                  <li
                    key={`${result.parcel_id || result.address}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={highlightedIndex === index}
                    onClick={() => handleSelect(result)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`
                      px-3 py-2 cursor-pointer transition-colors
                      ${highlightedIndex === index ? "bg-teal-50" : "hover:bg-gray-50"}
                    `}
                  >
                    <div className="flex items-start gap-2">
                      {/* Icon */}
                      <div
                        className={`
                        w-7 h-7 rounded flex items-center justify-center flex-shrink-0 mt-0.5
                        ${
                          highlightedIndex === index
                            ? "bg-teal-100 text-teal-600"
                            : "bg-gray-100 text-gray-500"
                        }
                      `}
                      >
                        <MapPin className="w-3.5 h-3.5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`
                          text-sm font-medium truncate
                          ${highlightedIndex === index ? "text-teal-900" : "text-gray-900"}
                        `}
                        >
                          {result.address}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                          {result.community && (
                            <span className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {result.community}
                            </span>
                          )}
                          {result.zone_code && (
                            <span
                              className={`
                              px-1.5 py-0.5 rounded text-xs font-medium
                              ${
                                highlightedIndex === index
                                  ? "bg-teal-100 text-teal-700"
                                  : "bg-gray-100 text-gray-600"
                              }
                            `}
                            >
                              {result.zone_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Footer hint */}
            {!isLoading && suggestions.length > 0 && (
              <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  Use{" "}
                  <kbd className="px-1 py-0.5 bg-white rounded border text-[10px]">
                    ↑
                  </kbd>{" "}
                  <kbd className="px-1 py-0.5 bg-white rounded border text-[10px]">
                    ↓
                  </kbd>{" "}
                  to navigate,{" "}
                  <kbd className="px-1 py-0.5 bg-white rounded border text-[10px]">
                    Enter
                  </kbd>{" "}
                  to select
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AddressAutocomplete;
